import { useState } from 'react';
import { Calculator, Send, TriangleAlert } from 'lucide-react';
import { EU_MEMBER_STATES } from '@kmdx/contracts';
import { cn } from '@/lib/cn';
import { t, countryName } from '@/i18n';
import { formatBps, formatDuration, formatKm, formatMinor } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import { useCalculateQuote, useSendQuote } from '@/api/hooks/quote';
import { usePartners } from '@/api/hooks/catalog';
import type { QuoteRequest } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, Stat } from '@/components/ui/Card';
import { DatePicker, Field, Input, Select } from '@/components/ui/Field';
import { toast } from '@/stores/toast';

const COUNTRY_OPTIONS = EU_MEMBER_STATES.map((code) => ({
  value: code,
  label: `${code} — ${countryName(code)}`,
}));

const DEFAULT_TARGET_MARGIN_BPS = 1800;

/**
 * Екранът с двете числа. Офертата в експреса се печели за минути — затова
 * всичко е на един екран и изчислението е едно натискане (Ctrl+Enter).
 *
 * Предупрежденията от ядрото се показват НАД цената, не под нея: „курсът е на
 * загуба" не бива да се вижда след като диспечерът вече е натиснал „изпрати".
 */
export function QuotePage() {
  const partners = usePartners();
  const calculate = useCalculateQuote();
  const send = useSendQuote();

  const [request, setRequest] = useState<QuoteRequest>({
    fromCountry: 'BG',
    fromCity: 'Пловдив',
    toCountry: 'DE',
    toCity: '',
  });
  const [partnerId, setPartnerId] = useState('');
  const [targetMarginBps, setTargetMarginBps] = useState(DEFAULT_TARGET_MARGIN_BPS);
  const [priceOverrideMinor, setPriceOverrideMinor] = useState<number | null>(null);

  const result = calculate.data;

  const runCalculation = () => {
    calculate.mutate(
      { ...request, targetMarginBps, partnerId: partnerId || undefined },
      { onSuccess: () => setPriceOverrideMinor(null) },
    );
  };

  useHotkeys([
    {
      keys: 'mod+Enter',
      description: t.quote.calculate,
      group: 'Оферта',
      allowInInput: true,
      run: runCalculation,
    },
  ]);

  const price = priceOverrideMinor ?? result?.suggestedPriceMinor ?? 0;
  const margin = result ? price - result.costMinor : 0;
  const marginBps = price > 0 ? Math.round((margin / price) * 10000) : 0;

  const update = (patch: Partial<QuoteRequest>) => setRequest((prev) => ({ ...prev, ...patch }));

  const sendQuote = () => {
    if (!result || !partnerId) {
      toast.warn('Изберете клиент', 'Офертата се изпраща на конкретен контрагент.');
      return;
    }
    send.mutate(
      {
        partnerId,
        request: { ...request, targetMarginBps },
        calculation: result,
        priceMinor: price,
      },
      {
        onSuccess: (quote) => toast.ok(t.quote.sent, quote.number),
      },
    );
  };

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4">
      <div>
        <h1 className="font-display text-lg font-bold tracking-tight">{t.quote.title}</h1>
        <p className="text-xs text-ink-dim">{t.quote.subtitle}</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        <Card>
          <CardHeader title={t.quote.lane} />
          <CardBody className="space-y-3">
            {/*
              9.5rem, а не 7.5: при 16 px шрифт (задължителни на телефон)
              „BG — България" иска ~152 px със стрелката. На 120 px падащото
              поле показваше „BG — Бълг" — държавата се четеше отрязана още
              преди да я отвориш. Градът отсреща остава 206 px, което стига.
            */}
            <div className="grid grid-cols-[9.5rem_1fr] gap-2 sm:grid-cols-[6rem_1fr]">
              <Field label="Държава">
                <Select
                  value={request.fromCountry}
                  onChange={(e) => update({ fromCountry: e.target.value })}
                  options={COUNTRY_OPTIONS}
                />
              </Field>
              <Field label={t.quote.fromPlace}>
                <Input
                  value={request.fromCity}
                  onChange={(e) => update({ fromCity: e.target.value })}
                />
              </Field>
              <Field label="Държава">
                <Select
                  value={request.toCountry}
                  onChange={(e) => update({ toCountry: e.target.value })}
                  options={COUNTRY_OPTIONS}
                />
              </Field>
              <Field label={t.quote.toPlace}>
                <Input
                  value={request.toCity}
                  onChange={(e) => update({ toCity: e.target.value })}
                />
              </Field>
            </div>

            {/*
              Датите излизат от двуколонната мрежа на телефон. `datetime-local`
              на Chrome иска ~200 px при 16 px шрифт, за да покаже и часа; в
              162 px колона се виждаше „dd.mm.yyyy," и нищо повече — точно
              часът, който решава дали бусът хваща прозореца, беше отрязан.
              От `sm` нагоре двете дати пак стоят една до друга.
            */}
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label={t.quote.readyAt}>
                <DatePicker
                  value={request.readyAt ?? ''}
                  onChange={(e) => update({ readyAt: e.target.value })}
                />
              </Field>
              <Field label={t.quote.deadlineAt}>
                <DatePicker
                  value={request.deadlineAt ?? ''}
                  onChange={(e) => update({ deadlineAt: e.target.value })}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Field label="Тегло (кг)">
                <Input
                  type="number"
                  min={1}
                  max={3500}
                  className="font-mono tabular"
                  value={request.weightKg ?? ''}
                  onChange={(e) => update({ weightKg: Number(e.target.value) || undefined })}
                />
              </Field>
              <Field label={`${t.quote.targetMargin} (%)`}>
                <Input
                  type="number"
                  min={0}
                  max={80}
                  step={0.5}
                  className="font-mono tabular"
                  value={targetMarginBps / 100}
                  onChange={(e) => setTargetMarginBps(Math.round(Number(e.target.value) * 100))}
                />
              </Field>
              <Field label="Разстояние (км)" hint="Празно = изчислява се">
                <Input
                  type="number"
                  min={0}
                  className="font-mono tabular"
                  value={request.distanceKm ?? ''}
                  onChange={(e) => update({ distanceKm: Number(e.target.value) || undefined })}
                />
              </Field>
              <Field label={`${t.quote.emptyKm} (км)`}>
                <Input
                  type="number"
                  min={0}
                  className="font-mono tabular"
                  value={request.emptyKm ?? ''}
                  onChange={(e) => update({ emptyKm: Number(e.target.value) || undefined })}
                />
              </Field>
            </div>

            <Field label={t.orders.partner}>
              <Select
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
                placeholder="— избери —"
                options={(partners.data?.items ?? [])
                  .filter((partner) => partner.isCustomer)
                  .map((partner) => ({ value: partner.id, label: partner.name }))}
              />
            </Field>

            <Button
              className="w-full"
              variant="primary"
              icon={<Calculator className="size-4" />}
              loading={calculate.isPending}
              onClick={runCalculation}
            >
              {t.quote.calculate}
              <kbd className="ml-2 hidden font-mono text-[10px] opacity-70 lg:inline">Ctrl ⏎</kbd>
            </Button>
          </CardBody>
        </Card>

        <div className="space-y-4">
          {result && result.warnings.length > 0 ? (
            <div className="rounded-lg border border-warn/50 bg-warn-soft p-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-warn">
                <TriangleAlert className="size-4" />
                {t.quote.warnings}
              </div>
              <ul className="space-y-1 text-sm text-ink">
                {result.warnings.map((warning) => (
                  <li key={warning}>• {warning}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <Card>
            <CardHeader
              title={t.quote.title}
              actions={
                <Button
                  variant="primary"
                  icon={<Send className="size-4" />}
                  disabled={!result}
                  loading={send.isPending}
                  onClick={sendQuote}
                >
                  {t.quote.send}
                </Button>
              }
            />
            <CardBody>
              {!result ? (
                <p className="py-8 text-center text-sm text-ink-dim">
                  Въведете направление и натиснете „{t.quote.calculate}".
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <Stat label={t.quote.distance} value={formatKm(result.distanceKm)} />
                    <Stat
                      label={t.quote.emptyKm}
                      value={formatKm(result.emptyKm)}
                      hint={
                        result.distanceKm > 0
                          ? `${Math.round((result.emptyKm / Math.max(result.distanceKm, 1)) * 100)} % от натоварените`
                          : undefined
                      }
                      tone={result.emptyKm > result.distanceKm * 0.3 ? 'warn' : 'default'}
                    />
                    <Stat label={t.quote.duration} value={formatDuration(result.durationMin)} />
                    <Stat label={t.quote.cost} value={formatMinor(result.costMinor, result.ccy)} />
                  </div>

                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div className="rounded-lg border border-line bg-raised p-3">
                      <div className="text-[11px] uppercase tracking-wide text-ink-faint">
                        {t.quote.suggestedPrice}
                      </div>
                      <input
                        type="number"
                        inputMode="decimal"
                        data-big
                        className="w-full bg-transparent font-mono text-3xl font-semibold tabular text-accent outline-none"
                        value={(price / 100).toFixed(2)}
                        step={10}
                        onChange={(e) =>
                          setPriceOverrideMinor(Math.round(Number(e.target.value) * 100))
                        }
                        aria-label={t.quote.suggestedPrice}
                      />
                      <div className="text-[11px] text-ink-faint">
                        {result.ccy} · може да се промени на ръка
                      </div>
                    </div>

                    <div
                      className={cn(
                        'rounded-lg border p-3',
                        margin < 0
                          ? 'border-danger/50 bg-danger-soft'
                          : marginBps < targetMarginBps
                            ? 'border-warn/50 bg-warn-soft'
                            : 'border-ok/50 bg-ok-soft',
                      )}
                    >
                      <div className="text-[11px] uppercase tracking-wide text-ink-faint">
                        {t.quote.margin}
                      </div>
                      <div
                        className={cn(
                          'font-mono text-3xl font-semibold tabular',
                          margin < 0
                            ? 'text-danger'
                            : marginBps < targetMarginBps
                              ? 'text-warn'
                              : 'text-ok',
                        )}
                      >
                        {formatMinor(margin, result.ccy)}
                      </div>
                      <div className="text-[11px] text-ink-dim">
                        {formatBps(marginBps)} · цел {formatBps(targetMarginBps)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="mb-1 text-[11px] uppercase tracking-wide text-ink-faint">
                      {t.quote.breakdown}
                    </div>
                    <ul className="divide-y divide-line text-sm">
                      {(
                        [
                          ['fuel', t.quote.fuel],
                          ['tolls', t.quote.tolls],
                          ['driver', t.quote.driver],
                          ['perDiem', t.quote.perDiem],
                          ['accommodation', t.quote.accommodation],
                          ['fixed', t.quote.fixed],
                          ['other', t.quote.other],
                        ] as const
                      ).map(([key, label]) => (
                        <li key={key} className="flex items-center justify-between py-1.5">
                          <span className="text-ink-dim">{label}</span>
                          <span className="font-mono tabular">
                            {formatMinor(result.breakdown[key], result.ccy)}
                          </span>
                        </li>
                      ))}
                      <li className="flex items-center justify-between py-1.5 font-medium">
                        <span>{t.common.total}</span>
                        <span className="font-mono tabular">
                          {formatMinor(result.costMinor, result.ccy)}
                        </span>
                      </li>
                    </ul>
                    <p className="mt-2 text-[11px] text-ink-faint">
                      Командировъчните се смятат по дни и държави, не по километри.
                    </p>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
